import React, { createContext, useCallback, useContext, useState } from 'react';
import { ToastContainer, ToastItem, ToastType } from '../components/Toast';

interface ShowToastOptions {
  type?: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  showToast: (opts: ShowToastOptions) => void;
}

const MAX_TOASTS = 3;

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ type = 'info', message, duration }: ShowToastOptions) => {
      const id = Date.now() + Math.random();
      // 오류는 읽을 시간을 더 준다. 동시 3개 초과면 가장 오래된 것부터 밀어낸다(무한 스택 방지)
      const ms = duration ?? (type === 'error' ? 6000 : 4000);
      setToasts((prev) => [...prev, { id, type, message }].slice(-MAX_TOASTS));
      if (ms > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, ms);
      }
    },
    []
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
